import type {StateCreator} from 'zustand';
import {List, Map} from 'immutable';
import type {AssignmentModelV3, DraftAssignmentModelV1} from '@albert-io/models';
import sessionStore from 'client/Session/SessionStore';

import {getAssignmentQuery} from 'client/components/ContentDiscovery/AssignmentSettings/AssignmentSettings.queries';
import type {ContentDiscoveryStoreState} from 'client/components/ContentDiscovery/store/ContentDiscoveryStore.types';

import type {SelectedRecipients} from '../../Document/Settings/RecipientSettings/RecipientSettings.types';

type AssignmentState = {
  assignment: AssignmentModelV3 | DraftAssignmentModelV1 | null;
  assignmentLoading: boolean;
  latestRequestId: number;
  assignmentResults: List<AssignmentModelV3 | DraftAssignmentModelV1>;
  selectedRecipients: SelectedRecipients; // recipients to display count on confirmation/share list
  isReassign: boolean;
  classrooms: List<any>;
  isDirty?: boolean;
  isNavigatingAway?: boolean;
  /**
   * Whether the loaded assignment was read without its students (#21182). The
   * settings form is built from that roster, so anything that populates or
   * submits it must wait for a full read rather than treat "no students" as
   * "no students assigned" — which would unassign the whole roster on save.
   */
  loadedWithoutStudents: boolean;
  /**
   * `assignment_builder_lazy_recipients`, pinned for the whole builder session.
   * null until the flag query resolves. Pinned so a form can never populate in
   * one mode and submit in the other.
   */
  lazyRecipients: boolean | null;
  /**
   * Students fetched by the lazy per-class list, keyed by classroom id (#21182). With the roster
   * includes gone, the teacher's classroom models carry no students, so this is the only place a
   * student's NAME can be read from — the per-student settings page needs it for its heading.
   */
  lazyStudentsByClassroom: Record<string, any[]>;
};

type AssignmentActions = {
  getAssignmentById: (
    assignmentId: string,
    isNowADraft: boolean | undefined,
    signal?: AbortSignal,
    omitStudents?: boolean
  ) => void;
  pinLazyRecipients: (enabled: boolean) => void;
  setLazyClassroomStudents: (classroomId: string, students: any[]) => void;
  setAssignmentResults: (assignmentResults: List<AssignmentModelV3>) => void;
  setSelectedRecipients: (selectedRecipients: SelectedRecipients) => void;
  reset: () => void;
  setIsReassign: (isReassign: boolean) => void;
  setClassrooms: (classrooms: List<any>) => void;
  setIsDirty: (isDirty: boolean) => void;
  setIsNavigatingAway: (isNavigatingAway: boolean) => void;
};

const initialState: AssignmentState = {
  assignment: null,
  assignmentLoading: false,
  latestRequestId: 0,
  assignmentResults: List(),
  selectedRecipients: Map(),
  isReassign: false,
  classrooms: List(),
  isDirty: false,
  isNavigatingAway: false,
  loadedWithoutStudents: false,
  lazyRecipients: null,
  lazyStudentsByClassroom: {}
};

export type AssignmentSlice = AssignmentState & AssignmentActions;

export const createAssignmentSlice: StateCreator<
  ContentDiscoveryStoreState,
  [['zustand/immer', never]],
  [],
  AssignmentSlice
> = (set, get): AssignmentSlice => ({
  ...initialState,
  // initial fetch of subjects by curriculum areas along with personalized and classroom subjects
  getAssignmentById: async (assignmentId, isNowADraft, signal, omitStudents = false) => {
    if (!assignmentId) {
      return;
    }

    const requestId = get().assignment.latestRequestId + 1;
    set((state) => {
      state.assignment.assignmentLoading = true;
      state.assignment.latestRequestId = requestId;
    });
    try {
      const isDraft = isNowADraft ?? get().draft.isDraft;
      const [fetchedAssignment] = await Promise.all([
        getAssignmentQuery(assignmentId, sessionStore.isSchoolAdministrator(), isDraft, signal, {
          omitStudents
        })
      ]);
      // A newer request started while this one was in flight; drop the stale result.
      if (get().assignment.latestRequestId !== requestId) {
        return;
      }
      set((state) => {
        state.assignment.assignmentLoading = false;
        state.assignment.assignment = fetchedAssignment;
        state.assignment.loadedWithoutStudents = omitStudents;
      });
    } catch (error) {
      // Aborted (builder unmounted or superseded by a newer fetch) — not a load failure;
      // don't clear state or rethrow, which would surface a stale error toast.
      if (signal?.aborted) {
        return;
      }
      if (get().assignment.latestRequestId !== requestId) {
        throw error;
      }
      set((state) => {
        state.assignment.assignmentLoading = false;
        state.assignment.assignment = null;
        state.assignment.loadedWithoutStudents = false;
      });
      throw error;
    }
  },

  // Pinned on first resolve only: a mid-session flag flip must not change the
  // mode the mounted form populated in.
  pinLazyRecipients: (enabled) =>
    set((state) => {
      if (state.assignment.lazyRecipients === null) {
        state.assignment.lazyRecipients = enabled;
      }
    }),

  setLazyClassroomStudents: (classroomId, students) =>
    set((state) => {
      state.assignment.lazyStudentsByClassroom[classroomId] = students;
    }),

  setAssignmentResults: (assignmentResults) =>
    set((state) => {
      state.assignment.assignmentResults = assignmentResults;
    }),

  setSelectedRecipients: (selectedRecipients) =>
    set((state) => {
      state.assignment.selectedRecipients = selectedRecipients;
    }),

  setIsReassign: (isReassign = true) =>
    set((state) => {
      state.assignment.isReassign = isReassign;
    }),

  setClassrooms: (classrooms) =>
    set((state) => {
      state.assignment.classrooms = classrooms;
    }),

  setIsDirty: (isDirty) =>
    set((state) => {
      state.assignment.isDirty = isDirty;
    }),

  setIsNavigatingAway: (isNavigatingAway) =>
    set((state) => {
      state.assignment.isNavigatingAway = isNavigatingAway;
    }),

  reset: () =>
    set((state) => {
      // The pinned mode survives: reset() is route-driven and fires mid-session (entering the
      // add-questions route), where clearing it strands every `lazyRecipients === null`-gated
      // fetch on a spinner that nothing re-arms (#21182).
      const {lazyRecipients} = state.assignment;
      state.assignment = {...state.assignment, ...initialState, lazyRecipients};
    })
});
